"use client";

import { Instagram, BookOpen, Music2 } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <ul className="space-y-3">
        {[0, 1, 2, 3].map(i => <li key={i} data-index={i} className="h-5 w-32 bg-white/10 animate-pulse rounded-md" />)}
      </ul>;
  }
  return <ul className="space-y-3">
      {menuItems.map((item, i) => <li data-index={i} key={item.id} className="flex items-center gap-3 border-b border-dashed border-white/15 pb-2">
          <span className="font-mono text-[0.6875rem] uppercase tracking-[0.22em] text-orange-500">
            § {String(i + 1).padStart(2, "0")}
          </span>
          <Link to={item.href || "#"} className="font-default text-base text-white hover:text-orange-500 transition-colors duration-300">
            {item.label}
          </Link>
        </li>)}
    </ul>;
}
const tickerItems = ["WILD-CAUGHT SEAFOOD", "BUILT BY HAND. PROVEN BY TIME.", "FROM SHORE TO DOOR", "SUSTAINABLE HARVEST", "WILD-CAUGHT SEAFOOD", "BUILT BY HAND. PROVEN BY TIME.", "FROM SHORE TO DOOR", "SUSTAINABLE HARVEST"];
const transmissions = [{
  label: "Instagram",
  icon: Instagram,
  href: "#"
}, {
  label: "Recipes",
  icon: BookOpen,
  href: "/RecipesPage"
}, {
  label: "Sustainability",
  icon: Music2,
  href: "/SustainabilityPage"
}];
const legalLinks = [{
  label: "Privacy",
  to: "#"
}, {
  label: "Terms",
  to: "#"
}, {
  label: "Imprint",
  to: "#"
}];
export default function Footer() {
  return <section data-section-id="1449" id="maritime-footer" className="dark relative overflow-hidden bg-slate-950 text-white pt-28 pb-12 border-t border-orange-500">
      <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-2/3 opacity-[0.05] bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1721626/img/nautical_map_texture.png)] bg-no-repeat bg-cover bg-bottom" />

      <div className="relative max-w-[1440px] mx-auto px-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12">
          <div className="md:col-span-5 space-y-6 relative">
            <div className="flex items-start justify-between font-mono text-[0.6875rem] uppercase tracking-[0.22em] text-white/50">
              <span>┌ 30°24′N</span>
              <span>88°49′W ┐</span>
            </div>

            <Link to="/" className="inline-block">
              <WvcLogo className="h-10 w-auto" />
            </Link>

            <p className="font-default text-lg leading-relaxed text-white/85 max-w-md">
              The Miss Yvonne is a heritage fishing vessel, built in 1965. We bring the bounty of the Gulf Coast directly to your table, honoring the traditions of sustainable fishing and expert craftsmanship.{" "}
              <em className="relative inline-block">
                Built by Hand.
                <span className="absolute left-0 -bottom-1 h-[2px] w-full bg-orange-500" />
              </em>{" "}
              Proven by time, delivered with care.
            </p>

            <div className="flex items-center gap-4 pt-2">
              <div className="relative h-16 w-16 rounded-md flex items-center justify-center bg-slate-900 border-2 border-orange-500 shadow-md">
                <div className="absolute inset-1 rounded-md border border-orange-500/60" />
                <span className="font-default text-2xl italic text-orange-500">MY</span>
              </div>
              <div className="font-mono text-[0.6875rem] uppercase tracking-[0.22em] text-white/70">
                <div>EST · MCMLXV</div>
                <div className="text-orange-500 mt-1">Ocean Springs</div>
              </div>
            </div>
          </div>

          <div className="md:col-span-3 space-y-6">
            <div className="space-y-2">
              <div className="font-mono text-[0.6875rem] uppercase tracking-[0.22em] text-orange-500">
                § NO. 01 — NAVIGATION
              </div>
              <h3 className="font-default text-xl text-white">
                <em className="relative inline-block">
                  Catalog
                  <span className="absolute left-0 -bottom-1 h-[2px] w-full bg-orange-500" />
                </em>
              </h3>
            </div>

            <MenuProvider menu_id="52" className="footer-menu">
              <FooterMenuConsumer />
            </MenuProvider>
          </div>

          <div className="md:col-span-2 space-y-6">
            <div className="space-y-2">
              <div className="font-mono text-[0.6875rem] uppercase tracking-[0.22em] text-orange-500">
                § NO. 02 — DISPATCH
              </div>
              <h3 className="font-default text-xl text-white">
                <em className="relative inline-block">
                  Contact
                  <span className="absolute left-0 -bottom-1 h-[2px] w-full bg-orange-500" />
                </em>
              </h3>
            </div>

            <address className="not-italic space-y-3 font-mono text-xs leading-relaxed tracking-[0.14em] uppercase text-white/80">
              <div>1965 Harbor Way</div>
              <div>Ocean Springs · MS</div>
              <div>USA</div>
              <div className="pt-2 text-orange-500">30°24′N · 88°49′W</div>
              <div className="pt-3 text-white/70">
                <Link to="tel:+12285550196" className="hover:text-orange-500 transition-colors">
                  +1 (228) 555-0196
                </Link>
              </div>
              <div className="text-white/70">
                <Link to="mailto:hello@missyvonne.com" className="hover:text-orange-500 transition-colors">
                  hello@missyvonne.com
                </Link>
              </div>
            </address>
          </div>

          <div className="md:col-span-2 space-y-6">
            <div className="space-y-2">
              <div className="font-mono text-[0.6875rem] uppercase tracking-[0.22em] text-orange-500">
                § NO. 03 — DISPATCH
              </div>
              <h3 className="font-default text-xl text-white">
                <em className="relative inline-block">
                  Social
                  <span className="absolute left-0 -bottom-1 h-[2px] w-full bg-orange-500" />
                </em>
              </h3>
            </div>

            <ul className="space-y-3">
              {transmissions.map((t, i) => {
              const Icon = t.icon;
              return <li data-index={i} key={t.label}>
                    <Link to={t.href} className="group flex items-center gap-3 font-mono text-xs uppercase tracking-[0.18em] text-white/80 hover:text-orange-500 transition-colors duration-300">
                      <Icon className="h-4 w-4 shrink-0" />
                      <span className="border-b border-dashed border-white/20 group-hover:border-orange-500 pb-0.5">
                        {t.label}
                      </span>
                    </Link>
                  </li>;
            })}
            </ul>
          </div>
        </div>

        <div className="mt-20 mb-12 relative">
          <div className="absolute inset-x-0 top-0 h-px bg-orange-500" aria-hidden="true" />
          <div className="absolute inset-x-0 bottom-0 h-px bg-orange-500" aria-hidden="true" />
          <div className="overflow-hidden py-4">
            <div className="flex whitespace-nowrap gap-12 animate-[ticker_40s_linear_infinite] hover:[animation-play-state:paused]" style={{
            animation: "ticker 40s linear infinite"
          }}>
              {[...tickerItems, ...tickerItems].map((item, i) => <span data-index={i} key={i} className="font-mono text-xs uppercase tracking-[0.22em] text-white/70 flex items-center gap-12">
                  <span className="text-orange-500">·</span>
                  {item}
                </span>)}
            </div>
          </div>
          <style>{`
            @keyframes ticker {
              0% { transform: translateX(0); }
              100% { transform: translateX(-50%); }
            }
          `}</style>
        </div>

        <div className="relative flex items-center gap-3 my-10" aria-hidden="true">
          <span className="h-1.5 w-1.5 rounded-md bg-orange-500 shrink-0" />
          <div className="flex-1 border-t border-dashed border-white/25" />
          <span className="h-1.5 w-1.5 rounded-md bg-orange-500 shrink-0" />
        </div>

        <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-8 pt-2">
          <div className="font-mono text-[0.6875rem] uppercase tracking-[0.22em] text-white/60">
            © THE MISS YVONNE · MMXXIV · ALL RIGHTS RESERVED
          </div>

          <div className="flex items-center gap-4 font-mono text-[0.6875rem] uppercase tracking-[0.22em]">
            {legalLinks.map((l, i) => <div data-index={i} key={l.label} className="flex items-center gap-4">
                <Link to={l.to} className="text-white/70 hover:text-orange-500 transition-colors">
                  {l.label}
                </Link>
                {i < legalLinks.length - 1 && <span className="text-orange-500">·</span>}
              </div>)}
          </div>

          <div className="relative -rotate-6 shrink-0 self-end md:self-auto" aria-hidden="true">
            <div className="relative px-5 py-3 border-2 border-orange-500 rounded-md">
              <div className="absolute inset-1 border border-orange-500/50 rounded-md" />
              <div className="relative font-mono text-[0.625rem] uppercase tracking-[0.25em] text-orange-500 text-center leading-tight">
                <div> HARVESTED IN </div>
                <div className="text-sm font-bold tracking-[0.2em] my-0.5">GULF COAST</div>
                <div>· USA ·</div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 flex items-end justify-end font-mono text-[0.6875rem] uppercase tracking-[0.22em] text-white/40">
          <span>└ END OF LOG ┘</span>
        </div>
      </div>
    </section>;
}